import React, { useState, useEffect } from 'react';
import { motion, useMotionValue, useAnimationControls } from 'motion/react';
import { Quote, Star, Sparkles, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

// Testimonial Interface
export interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  content: string;
  rating: number;
  avatar: string;
  industry: string;
  color: string;
  impact: string;
}

// Component Props Interface
interface ProductTestimonialsProps {
  testimonials: Testimonial[];
  title: string;
  subtitle: string;
  gradientFrom: string;
  gradientVia: string;
  gradientTo: string;
  iconColor: string;
  dotColor: string;
  speed?: number;
  direction?: 'left' | 'right';
}

// Testimonial Card Component
const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Card
      className="glass-strong w-[380px] h-full group hover:scale-[1.02] transition-all duration-300 relative flex-shrink-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="absolute top-0 left-0 right-0 h-1 transition-all duration-300"
        style={{ backgroundColor: testimonial.color }}
      />
      
      <motion.div
        className="absolute inset-0 opacity-0 transition-opacity duration-500 blur-xl pointer-events-none"
        animate={{ opacity: isHovered ? 1 : 0 }}
        style={{
          background: `radial-gradient(circle at top, ${testimonial.color}30, transparent 70%)`
        }}
      />

      <CardContent className="p-6 pb-8 relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-full overflow-hidden ring-2 transition-all duration-300"
              style={{ borderColor: isHovered ? testimonial.color : 'rgba(255,255,255,0.2)' }}
            >
              <img
                src={testimonial.avatar}
                alt={testimonial.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h4 className="font-bold">{testimonial.name}</h4>
              <p className="text-xs text-muted-foreground">{testimonial.role}</p>
            </div>
          </div>
          <motion.div
            className="p-2 rounded-lg"
            style={{ backgroundColor: `${testimonial.color}20` }}
            animate={{ rotate: isHovered ? 360 : 0 }}
            transition={{ duration: 0.6 }}
          >
            <Quote className="w-4 h-4" style={{ color: testimonial.color }} />
          </motion.div>
        </div>

        <div className="mb-4">
          <p className="font-semibold text-sm">{testimonial.company}</p>
          <span
            className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{
              backgroundColor: `${testimonial.color}20`,
              color: testimonial.color
            }}
          >
            {testimonial.industry}
          </span>
        </div>

        <div className="flex gap-1 mb-4">
          {[...Array(testimonial.rating)].map((_, i) => (
            <Star
              key={i}
              className="w-4 h-4 fill-current"
              style={{ color: testimonial.color }}
            />
          ))}
        </div>

        <blockquote className="text-sm text-muted-foreground mb-4 flex-grow text-justify">
          "{testimonial.content}"
        </blockquote>

        <div className="flex items-center gap-2 pt-4 border-t border-white/10">
          <TrendingUp className="w-4 h-4" style={{ color: testimonial.color }} />
          <span className="text-xs font-semibold" style={{ color: testimonial.color }}>
            {testimonial.impact}
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// Marquee Row Component
const MarqueeRow: React.FC<{
  testimonials: Testimonial[];
  direction?: 'left' | 'right';
  speed?: number;
  dotColor: string;
}> = ({ testimonials, direction = 'left', speed = 40, dotColor }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const x = useMotionValue(0);
  const controls = useAnimationControls();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 3000); // Changed from 5000 to 3000 (3 seconds)
    
    return () => clearInterval(interval);
  }, [isMobile, isPaused, testimonials.length]);

  const duplicatedTestimonials = [...testimonials, ...testimonials, ...testimonials];
  const totalDistance = testimonials.length * 386 * 2;

  useEffect(() => {
    if (isMobile) return;
    
    const startAnimation = async () => {
      const currentX = x.get();
      const targetX = direction === 'left' ? -totalDistance : 0;
      const startX = direction === 'left' ? 0 : -totalDistance;
      
      let progress: number;
      if (direction === 'left') {
        progress = Math.abs(currentX) / totalDistance;
      } else {
        progress = (totalDistance - Math.abs(currentX)) / totalDistance;
      }
      
      progress = progress % 1;
      const remainingTime = speed * (1 - progress);
      
      if (Math.abs(currentX) < 10 || progress > 0.99) {
        x.set(startX);
        await controls.start({
          x: targetX,
          transition: {
            duration: speed,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          },
        });
      } else {
        await controls.start({
          x: targetX,
          transition: {
            duration: remainingTime,
            ease: 'linear',
          },
        });
        
        x.set(startX);
        await controls.start({
          x: targetX,
          transition: {
            duration: speed,
            ease: 'linear',
            repeat: Infinity,
            repeatType: 'loop',
          },
        });
      }
    };

    if (!isPaused) {
      startAnimation();
    } else {
      controls.stop();
    }

    return () => {
      controls.stop();
    };
  }, [isPaused, controls, x, speed, direction, totalDistance, isMobile]);

  if (isMobile) {
    const handleDragEnd = ( info: any) => {
      const swipeThreshold = 50;
      
      if (info.offset.x > swipeThreshold) {
        // Swiped right - go to previous
        setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 3000);
      } else if (info.offset.x < -swipeThreshold) {
        // Swiped left - go to next
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
        setIsPaused(true);
        setTimeout(() => setIsPaused(false), 3000);
      }
    };

    return (
      <div className="relative px-4">
        <div className="flex justify-center items-center overflow-hidden">
          <motion.div
            key={currentIndex}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-[380px] cursor-grab active:cursor-grabbing"
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => {
              setTimeout(() => setIsPaused(false), 3000);
            }}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="h-[380px]">
              <TestimonialCard testimonial={testimonials[currentIndex]} />
            </div>
          </motion.div>
        </div>
        
        <div className="flex justify-center gap-2 mt-6">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index);
                setIsPaused(true);
                setTimeout(() => setIsPaused(false), 3000);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? `${dotColor} w-8`
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative overflow-hidden py-3"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div
        className="flex gap-6"
        drag="x"
        dragConstraints={{ left: -4000, right: 100 }}
        dragElastic={0.1}
        dragMomentum={false}
        style={{ 
          x,
          cursor: 'grab',
          userSelect: 'none'
        }}
        animate={controls}
        onDragStart={() => setIsPaused(true)}
        onDragEnd={() => {
          setTimeout(() => setIsPaused(false), 500);
        }}
        whileTap={{ cursor: 'grabbing' }}
      >
        {duplicatedTestimonials.map((testimonial, index) => (
          <div key={`${testimonial.id}-${index}`} className="h-[380px]">
            <TestimonialCard testimonial={testimonial} />
          </div>
        ))}
      </motion.div>
    </div>
  );
};

// Main Product Testimonials Component
export const ProductTestimonials: React.FC<ProductTestimonialsProps> = ({
  testimonials,
  title,
  subtitle,
  gradientFrom,
  gradientVia,
  gradientTo,
  iconColor,
  dotColor,
  speed = 65,
  direction = 'left'
}) => {
  return (
    <section className="py-15 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
      
      <motion.div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
        style={{ backgroundColor: `${gradientFrom}10` }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl pointer-events-none"
        style={{ backgroundColor: `${gradientVia}10` }}
        animate={{
          scale: [1.2, 1, 1.2],
          opacity: [0.5, 0.3, 0.5],
        }}
        transition={{ duration: 8, repeat: Infinity, delay: 1 }}
      />
      
      <div className="max-w-full relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-10 px-4"
        >
          <div className="flex items-center justify-center mb-4">
            <Sparkles className="w-8 h-8 mr-3 animate-pulse" style={{ color: iconColor }} />
            <h2 className="mb-0">
              <span 
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: `linear-gradient(to right, ${gradientFrom}, ${gradientVia}, ${gradientTo})`
                }}
              >
                {title}
              </span>{' '}
              Stories
            </h2>
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            {subtitle}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="space-y-0"
        >
          <MarqueeRow 
            testimonials={testimonials} 
            direction={direction} 
            speed={speed}
            dotColor={dotColor}
          />
        </motion.div>

        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-background to-transparent pointer-events-none z-20" />
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-background to-transparent pointer-events-none z-20" />
      </div>
    </section>
  );
};

export default ProductTestimonials;